import type { IconName } from "../../icons";

export type ToastTone = "success" | "info" | "warning" | "danger";

export interface ToastOptions {
  title: string;
  text?: string;
  tone?: ToastTone;
  /** Milliseconds; errors stay a bit longer by default. */
  duration?: number;
}

export interface ToastEntry extends ToastOptions {
  id: number;
  leaving?: boolean;
}

/** At most this many toasts on screen; a new one pushes out the oldest. */
export const MAX_TOASTS = 4;
export const DEFAULT_DURATION_MS = 4500;
export const DANGER_DURATION_MS = 7000;
/** Matches the exit animation in Toast.css. */
export const LEAVE_ANIMATION_MS = 160;
export const DEFAULT_TONE: ToastTone = "info";

export const TOAST_ICON: Record<ToastTone, IconName> = {
  success: "checkCircle",
  info: "info",
  warning: "alert",
  danger: "alertCircle",
};

export function toastTone(options: ToastOptions): ToastTone {
  return options.tone ?? DEFAULT_TONE;
}

/** How long the toast stays: its own duration, or longer for errors. */
export function toastDuration(options: ToastOptions): number {
  return options.duration ?? (options.tone === "danger" ? DANGER_DURATION_MS : DEFAULT_DURATION_MS);
}

/** An error interrupts the screen reader; the rest wait their turn. */
export function toastRole(tone: ToastTone): "alert" | "status" {
  return tone === "danger" ? "alert" : "status";
}

export function addToast(toasts: ToastEntry[], toast: ToastEntry): ToastEntry[] {
  return [...toasts, toast].slice(-MAX_TOASTS);
}

export function markLeaving(toasts: ToastEntry[], id: number): ToastEntry[] {
  return toasts.map((toast) => (toast.id === id ? { ...toast, leaving: true } : toast));
}

export function removeToast(toasts: ToastEntry[], id: number): ToastEntry[] {
  return toasts.filter((toast) => toast.id !== id);
}
